/**
 * Sign-off lifecycle — shared between web (and later mobile) so the
 * status labels, config shape and comment payload stay consistent
 * across every consumer of the form-fill engine.
 *
 * A form document can opt in to a sign-off step. Once enabled, a
 * submitted result waits for a designated reviewer to mark it
 * Satisfactory or Unsatisfactory. Results on forms WITHOUT sign-off
 * never leave the `none` state, so existing dashboards are unaffected.
 *
 * Status progression (sign-off enabled):
 *
 *   none ──submit──▶ awaiting ──review──▶ satisfactory
 *                              └─────────▶ unsatisfactory
 */

/**
 * Catalogue of sign-off statuses. The numeric `value` mirrors the
 * `signoff_status` smallint persisted by ISM
 * (0 none / 1 awaiting / 2 satisfactory / 3 unsatisfactory).
 */
export const SIGNOFF_STATUSES = [
  { id: "none", value: 0, label: "" },
  { id: "awaiting", value: 1, label: "Completed – Awaiting Sign-off" },
  { id: "satisfactory", value: 2, label: "Satisfactory" },
  { id: "unsatisfactory", value: 3, label: "Unsatisfactory" },
] as const;

export type SignoffStatusId = (typeof SIGNOFF_STATUSES)[number]["id"];

/** Verdict a reviewer can record. */
export type SignoffVerdict = "satisfactory" | "unsatisfactory";

/**
 * Default decision labels. The verdict itself is a stable two-state value
 * (satisfactory / unsatisfactory); a form may rename how each side is shown
 * to reviewers and on reports via `signoffAcceptLabel` / `signoffDeclineLabel`.
 * When unset, these defaults preserve today's wording.
 */
export const SIGNOFF_ACCEPT_LABEL_DEFAULT = "Satisfactory";
export const SIGNOFF_DECLINE_LABEL_DEFAULT = "Unsatisfactory";

/** Which party the configured reviewer belongs to. */
export type SignoffReviewerParty = "customer" | "internal";

/** Whether the reviewer is a user group or one specific user. */
export type SignoffReviewerKind = "group" | "user";

/**
 * A single step in the reviewer chain. The chain is walked top-down at
 * submission time; the first step that resolves to an eligible reviewer
 * wins. `[0]` is the primary; the rest are ordered fallbacks.
 *
 *  - `"linked-contact-role"` — dynamic: resolve the contact linked (by
 *    `role`) to the managed property the form result is attached to.
 *  - `"user"` / `"group"` — static: a specific client-user or user group,
 *    interpreted within `party` (and `customerId` for the customer party),
 *    exactly like the legacy single-reviewer config.
 *  - `"email-address"` — a plain email address; the reviewer needn't have an
 *    Eaco login. They're emailed a tokenised sign-off link (same channel as a
 *    resolved linked contact).
 */
export type SignoffStepType =
  | "linked-contact-role"
  | "user"
  | "group"
  | "email-address";

export interface SignoffReviewerStep {
  type: SignoffStepType;
  /**
   * Set when type = linked-contact-role: which of the form result's linked
   * entities to resolve the contact against (e.g. "MANAGED_PROPERTY",
   * "JOB"). Defaults to MANAGED_PROPERTY when absent.
   */
  entityType?: string | null;
  /** Set when type = linked-contact-role, e.g. "SIGNOFF_CONTACT". */
  role?: string | null;
  /** Set for static steps (user | group). */
  party?: SignoffReviewerParty | null;
  /** Set for a static step on the customer party. */
  customerId?: string | null;
  /** Set when type = user. */
  userId?: string | null;
  /** Set when type = group. */
  groupId?: string | null;
  /** Set when type = email-address — the reviewer's email. */
  email?: string | null;
}

/**
 * Sign-off settings stored on the form document (alongside
 * `requireTurnstile`, `scoreThresholds`, ...).
 */
export interface SignoffConfig {
  /** When false the result lifecycle is the legacy Partial → Submitted. */
  signoffEnabled: boolean;
  /** Customer (a specific customer org) or internal ("ourselves"). */
  signoffReviewerParty: SignoffReviewerParty;
  /**
   * The customer whose reviewer signs off, when party = customer.
   * Required for the customer party; null for the internal party. The
   * configured group/user is interpreted within this customer's org.
   */
  signoffReviewerCustomerId: string | null;
  /** Resolve the reviewer to a group or a single user. */
  signoffReviewerKind: SignoffReviewerKind;
  /** Set when kind = group. */
  signoffReviewerGroupId: string | null;
  /** Set when kind = user. */
  signoffReviewerUserId: string | null;
  /**
   * Ordered reviewer chain (primary + fallbacks). The source of truth for
   * new configs; the flat `signoffReviewer*` fields above remain for
   * back-compat reads. Empty/absent → fall back to `legacyReviewerToChain`.
   */
  signoffReviewerChain?: SignoffReviewerStep[];
  /**
   * Optional number of days after submission that the sign-off is due.
   * Null/absent (or <= 0) means no due date.
   */
  signoffDueDays?: number | null;
  /**
   * Whether the sign-off reviewer may see the result score. Default true
   * (visible). Consumed by the sign-off hub.
   */
  signoffReviewerCanSeeScore?: boolean;
  /**
   * Whether the sign-off reviewer may see flagged answers. Default true
   * (visible). Consumed by the sign-off hub.
   */
  signoffReviewerCanSeeFlags?: boolean;
  /**
   * A title shown to the person signing off and on the rendered report —
   * e.g. their role or the party they represent ("Client Representative").
   * Null/absent → no title shown.
   */
  signoffReviewerTitle?: string | null;
  /**
   * Label for the positive decision. Display-only rename over the stable
   * `satisfactory` verdict. Null/absent → `SIGNOFF_ACCEPT_LABEL_DEFAULT`.
   */
  signoffAcceptLabel?: string | null;
  /**
   * Label for the negative decision. Display-only rename over the stable
   * `unsatisfactory` verdict. Null/absent → `SIGNOFF_DECLINE_LABEL_DEFAULT`.
   */
  signoffDeclineLabel?: string | null;
  /**
   * Whether the reviewer may leave a note against a single question's answer.
   * Default true (allowed).
   */
  signoffReviewerCanComment?: boolean;
  /**
   * Whether the reviewer may add an overall comment about the whole response.
   * Default true (allowed). When false, `signoffRequireNegativeComment` has
   * no effect.
   */
  signoffReviewerCanOverallComment?: boolean;
  /**
   * Whether the reviewer must leave an overall comment before recording the
   * negative decision. Default true (required), matching legacy behaviour.
   */
  signoffRequireNegativeComment?: boolean;
  /**
   * When true the sign-off is acknowledgement-only: the reviewer can only
   * record the positive outcome (the response is always resolved
   * `satisfactory`). The negative/decline path is removed everywhere — the
   * decline label, presets and require-negative-comment no longer apply.
   * Default false (a two-way decision: accept or decline).
   */
  signoffAcknowledgementOnly?: boolean;
}

/**
 * A file the reviewer attached at sign-off — a photo of what they are
 * commenting on. `fileId` is the attachment the upload produced; `name`
 * is what it was called, kept so a list reads as something other than
 * numbers.
 */
export interface SignoffAttachment {
  fileId: number;
  name: string;
}

/**
 * Reviewer evidence captured at sign-off time. `overall` is the single
 * result-level note; `elements` keys per-element notes by element id.
 * A comment is optional for Satisfactory, mandatory for Unsatisfactory.
 *
 * Photos are additive keys rather than a richer `elements` value, because
 * four readers (this parser, the portal panel, the PHP entity and the
 * output render) rebuild this shape field by field and silently drop what
 * they do not recognise. Keeping `overall` and `elements` exactly as they
 * were means a reader that has not been taught about attachments still
 * returns correct comments instead of nothing.
 */
export interface SignoffComments {
  overall: string;
  elements: Record<string, string>;
  /** Photos against the response as a whole. */
  overallAttachments?: SignoffAttachment[];
  /** Photos against a single answer, keyed by element id. */
  elementAttachments?: Record<string, SignoffAttachment[]>;
}

/** Narrow unknown JSON to a list of attachments, dropping anything malformed. */
export function readSignoffAttachments(value: unknown): SignoffAttachment[] {
  if (!Array.isArray(value)) return [];
  const out: SignoffAttachment[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const fileId = Number((entry as { fileId?: unknown }).fileId);
    if (!Number.isInteger(fileId) || fileId <= 0) continue;
    const rawName = (entry as { name?: unknown }).name;
    out.push({ fileId, name: typeof rawName === "string" ? rawName : "" });
  }
  return out;
}

/** The same, for the per-element map. Elements with no usable file are dropped. */
export function readSignoffAttachmentMap(
  value: unknown,
): Record<string, SignoffAttachment[]> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, SignoffAttachment[]> = {};
  for (const [elementId, list] of Object.entries(value as Record<string, unknown>)) {
    const files = readSignoffAttachments(list);
    if (files.length) out[elementId] = files;
  }
  return out;
}

/** Map a sign-off status id to its user-facing label. */
export function signoffStatusLabel(id: SignoffStatusId): string {
  return SIGNOFF_STATUSES.find((s) => s.id === id)?.label ?? "";
}

/** Map the persisted `signoff_status` smallint to its status id. */
export function signoffStatusFromValue(value: number | null | undefined): SignoffStatusId {
  return SIGNOFF_STATUSES.find((s) => s.value === value)?.id ?? "none";
}

/**
 * Derive the user-facing status label for a result, folding the
 * sign-off lifecycle over the base submitted/draft status. `none`
 * falls through to the supplied base label so non-sign-off forms keep
 * today's behaviour.
 *
 * For a recorded verdict (satisfactory / unsatisfactory) the snapshotted
 * `outcomeLabel` (captured at sign-off) is preferred when present — so lists
 * and detail views show the form's configured decision wording ("Approved")
 * with no form-config lookup, and past decisions keep their original wording.
 */
export function resultStatusLabel(opts: {
  signoffStatus: SignoffStatusId;
  /** Label to use when signoffStatus is `none` (e.g. "Submitted"). */
  baseLabel: string;
  /** Decision label snapshotted onto the result at sign-off time, if any. */
  outcomeLabel?: string | null;
}): string {
  if (opts.signoffStatus === "none") {
    return opts.baseLabel;
  }
  if (
    (opts.signoffStatus === "satisfactory" ||
      opts.signoffStatus === "unsatisfactory") &&
    opts.outcomeLabel?.trim()
  ) {
    return opts.outcomeLabel.trim();
  }
  return signoffStatusLabel(opts.signoffStatus);
}

/** Resolve the positive decision label for a config (falls back to default). */
export function resolveSignoffAcceptLabel(
  config?: Pick<SignoffConfig, "signoffAcceptLabel"> | null,
): string {
  return config?.signoffAcceptLabel?.trim() || SIGNOFF_ACCEPT_LABEL_DEFAULT;
}

/** Resolve the negative decision label for a config (falls back to default). */
export function resolveSignoffDeclineLabel(
  config?: Pick<SignoffConfig, "signoffDeclineLabel"> | null,
): string {
  return config?.signoffDeclineLabel?.trim() || SIGNOFF_DECLINE_LABEL_DEFAULT;
}

/** The configured display label for a recorded verdict. */
export function signoffVerdictLabel(
  verdict: SignoffVerdict,
  config?: Pick<
    SignoffConfig,
    "signoffAcceptLabel" | "signoffDeclineLabel"
  > | null,
): string {
  return verdict === "satisfactory"
    ? resolveSignoffAcceptLabel(config)
    : resolveSignoffDeclineLabel(config);
}

/**
 * Whether the form's sign-off is acknowledgement-only — the reviewer can only
 * record the positive outcome; there is no decline. Consumers use this to hide
 * the negative decision (button, label, require-comment) everywhere.
 */
export function signoffIsAcknowledgementOnly(
  config?: Pick<SignoffConfig, "signoffAcknowledgementOnly"> | null,
): boolean {
  return !!config?.signoffAcknowledgementOnly;
}

/**
 * Whether a verdict requires a non-empty overall comment before it can be
 * recorded. The negative verdict requires one when the form opts in
 * (`signoffRequireNegativeComment`, default true) AND overall comments are
 * allowed at all. Enforced client-side for UX; ISM remains the source of truth.
 */
export function signoffCommentRequired(
  verdict: SignoffVerdict,
  config?: Pick<
    SignoffConfig,
    | "signoffRequireNegativeComment"
    | "signoffReviewerCanOverallComment"
    | "signoffAcknowledgementOnly"
  > | null,
): boolean {
  if (verdict !== "unsatisfactory") {
    return false;
  }
  // Acknowledgement-only forms have no negative decision to require a comment for.
  if (config?.signoffAcknowledgementOnly) {
    return false;
  }
  if (config?.signoffReviewerCanOverallComment === false) {
    return false;
  }
  return config?.signoffRequireNegativeComment ?? true;
}

/**
 * Map a legacy flat reviewer config into a one-step chain, so a config
 * saved before the chain existed still resolves. Returns `[]` when no
 * static reviewer target is configured (nothing to sign off to).
 *
 * `signoffReviewerChain` (when present and non-empty) is the source of
 * truth; callers use this only as the fallback for legacy rows.
 */
export function legacyReviewerToChain(
  config: Pick<
    SignoffConfig,
    | "signoffReviewerParty"
    | "signoffReviewerCustomerId"
    | "signoffReviewerKind"
    | "signoffReviewerGroupId"
    | "signoffReviewerUserId"
  >,
): SignoffReviewerStep[] {
  const isGroup = config.signoffReviewerKind === "group";
  const targetId = isGroup
    ? config.signoffReviewerGroupId
    : config.signoffReviewerUserId;
  if (!targetId) {
    return [];
  }
  return [
    {
      type: isGroup ? "group" : "user",
      party: config.signoffReviewerParty ?? null,
      customerId: config.signoffReviewerCustomerId ?? null,
      groupId: isGroup ? config.signoffReviewerGroupId : null,
      userId: isGroup ? null : config.signoffReviewerUserId,
    },
  ];
}

/**
 * The effective reviewer chain for a config: the explicit
 * `signoffReviewerChain` when it has entries, else the legacy flat
 * fields folded into a single step.
 */
export function effectiveReviewerChain(
  config: SignoffConfig,
): SignoffReviewerStep[] {
  if (config.signoffReviewerChain && config.signoffReviewerChain.length > 0) {
    return config.signoffReviewerChain;
  }
  return legacyReviewerToChain(config);
}
